// Leveransdatum (frontend-spegling av supabase/functions/_shared/delivery-dates.ts).
// Avgång den 1:a och den 15:e, framflyttad till närmaste svenska arbetsdag.
// Efter avgången levereras paketet inom 5–7 arbetsdagar.

export const DELIVERY_WINDOW_BUSINESS_DAYS_MIN = 5;
export const DELIVERY_WINDOW_BUSINESS_DAYS_MAX = 7;

function ymd(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function parse(dateStr: string): Date {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

function addDays(d: Date, n: number): Date {
  const out = new Date(d.getTime());
  out.setUTCDate(out.getUTCDate() + n);
  return out;
}

// Påskdagen (gregoriansk, Meeus/Jones/Butcher).
function easterSunday(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(Date.UTC(year, month - 1, day));
}

const holidayCache = new Map<number, Set<string>>();

export function swedishHolidays(year: number): Set<string> {
  const cached = holidayCache.get(year);
  if (cached) return cached;
  const easter = easterSunday(year);
  const days: Date[] = [
    new Date(Date.UTC(year, 0, 1)),
    new Date(Date.UTC(year, 0, 6)),
    addDays(easter, -2),
    easter,
    addDays(easter, 1),
    new Date(Date.UTC(year, 4, 1)),
    addDays(easter, 39),
    new Date(Date.UTC(year, 5, 6)),
    new Date(Date.UTC(year, 11, 24)),
    new Date(Date.UTC(year, 11, 25)),
    new Date(Date.UTC(year, 11, 26)),
    new Date(Date.UTC(year, 11, 31)),
  ];
  for (let d = 19; d <= 25; d++) {
    const cand = new Date(Date.UTC(year, 5, d));
    if (cand.getUTCDay() === 5) {
      days.push(cand, addDays(cand, 1));
      break;
    }
  }
  const set = new Set(days.map(ymd));
  holidayCache.set(year, set);
  return set;
}

export function isBusinessDay(dateStr: string): boolean {
  const d = parse(dateStr);
  const dow = d.getUTCDay();
  if (dow === 0 || dow === 6) return false;
  return !swedishHolidays(d.getUTCFullYear()).has(dateStr);
}

/** Lägg till N arbetsdagar på ett datum (helger och röda dagar hoppas över). */
export function addBusinessDays(dateStr: string, n: number): string {
  let d = parse(dateStr);
  let left = n;
  let guard = 0;
  while (left > 0 && guard++ < 400) {
    d = addDays(d, 1);
    if (isBusinessDay(ymd(d))) left -= 1;
  }
  return ymd(d);
}

/** Leveransfönster: 5–7 arbetsdagar efter avgången. */
export function deliveryWindow(departure: string): { from: string; to: string } {
  const dep = departure.slice(0, 10);
  return {
    from: addBusinessDays(dep, DELIVERY_WINDOW_BUSINESS_DAYS_MIN),
    to: addBusinessDays(dep, DELIVERY_WINDOW_BUSINESS_DAYS_MAX),
  };
}

/** Leveransfönster som text, t.ex. "8–10 okt". */
export function deliveryWindowLabel(departure: string | null | undefined): string | null {
  if (!departure) return null;
  const { from: fromStr, to: toStr } = deliveryWindow(departure);
  const from = parse(fromStr);
  const to = parse(toStr);
  const fmtDay = (d: Date) => d.getUTCDate();
  const fmtMonth = (d: Date) =>
    d.toLocaleDateString("sv-SE", { month: "short", timeZone: "UTC" }).replace(".", "");
  if (from.getUTCMonth() === to.getUTCMonth()) {
    return `${fmtDay(from)}–${fmtDay(to)} ${fmtMonth(to)}`;
  }
  return `${fmtDay(from)} ${fmtMonth(from)}–${fmtDay(to)} ${fmtMonth(to)}`;
}

export type DeliveryAnchor = 1 | 15;

/** Samma dag om det är en arbetsdag, annars nästa arbetsdag framåt. */
export function nextBusinessDay(dateStr: string): string {
  let d = parse(dateStr);
  for (let i = 0; i < 30; i++) {
    const s = ymd(d);
    if (isBusinessDay(s)) return s;
    d = addDays(d, 1);
  }
  return ymd(d);
}

/** Avgångsdatum för en given månad + anchor, framflyttad till arbetsdag. */
export function departureFor(year: number, monthZeroIdx: number, anchor: DeliveryAnchor): string {
  return nextBusinessDay(ymd(new Date(Date.UTC(year, monthZeroIdx, anchor))));
}

/** Vilken avgång hör ett planerat datum till? */
export function anchorFromPlannedDate(dateStr: string | null | undefined): DeliveryAnchor {
  if (!dateStr) return 1;
  const day = parse(dateStr.slice(0, 10)).getUTCDate();
  return day >= 8 && day <= 21 ? 15 : 1;
}

/** Kommande avgångar (strikt efter `from`) för en anchor. */
export function upcomingDepartures(
  from: string,
  anchor: DeliveryAnchor,
  count: number,
): string[] {
  const f = parse(from.slice(0, 10));
  let y = f.getUTCFullYear();
  let m = f.getUTCMonth();
  const out: string[] = [];
  for (let i = 0; i < count + 14 && out.length < count; i++) {
    const cand = departureFor(y, m, anchor);
    if (cand > from.slice(0, 10)) out.push(cand);
    m += 1;
    if (m > 11) { m = 0; y += 1; }
  }
  return out;
}
